/**
 * AsyncPanel — shared loading/error/empty wrapper for portal screens.
 *
 * The portal does not use route loaders for data (ARCH-007 leaves the choice
 * to the component; we fetch via supabase-js in an effect, matching the
 * established billing.tsx pattern). This component standardizes the three
 * non-happy states so every screen handles them consistently
 * (ui-structure.md §Loading, error, and empty states).
 */

import type { ReactNode } from 'react';

export type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: T };

export function AsyncPanel<T>({
  state,
  children,
  loadingLabel = 'Loading...',
}: {
  state: AsyncState<T>;
  children: (data: T) => ReactNode;
  loadingLabel?: string;
}) {
  if (state.status === 'loading') {
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center text-sm text-[color:var(--color-text-mute)]"
        role="status"
        aria-live="polite"
      >
        {loadingLabel}
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div
        className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-linen)] p-6 text-sm text-[color:var(--color-navy)]"
        role="alert"
      >
        <p className="mb-1 font-medium">Something did not load.</p>
        <p className="text-[color:var(--color-text-mute)]">{state.message}</p>
      </div>
    );
  }
  return <>{children(state.data)}</>;
}
